import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  SimpleGrid,
  Text,
  useColorModeValue,
  useDisclosure,
  Spinner,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  AspectRatio,
  Skeleton
} from '@chakra-ui/react';
import { useRenderedVideosQuery } from 'store/aiHub/aiGenerationApi';
import UploadRenderVideosModal from './UploadRenderVideosModal';
import Pagination from 'components/drive/PaginationForDrive';
import { TbRefresh } from 'react-icons/tb';
import RenderedVideoCard from './RenderedVideoCard';

function OutputGenerated() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [loaded, setLoaded] = useState(false);

  const {
    isOpen: isPreviewOpen,
    onOpen: onPreviewOpen,
    onClose: onPreviewClose,
  } = useDisclosure();
  const [selectedPreview, setSelectedPreview] = useState(null);

  const ring = useColorModeValue('#40225f', '#fe72ff');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  const [currentPage, setCurrentPage] = useState(1);

  // dynamic list-level polling (0 or 10s)
  const [pollMs, setPollMs] = useState(0);

  const {
    data: renderedVideos,
    isLoading,
    isFetching,
    refetch
  } = useRenderedVideosQuery(
    { currentPage },
    {
      pollingInterval: pollMs,
      refetchOnFocus: Boolean(pollMs),
      refetchOnReconnect: Boolean(pollMs),
    }
  );

  const meta = renderedVideos?.meta || {};
  const pageSize = Number(meta?.pageSize || 10);
  const totalItems = Number(meta?.totalItems || 0);
  const items = renderedVideos?.data ?? [];
  const hasVideos = items.length > 0;

  const PROCESSING_STATES = useMemo(() => ['pending', 'in_queue', 'running'], []);

  const shouldPoll = useMemo(() => {
    if (!hasVideos) return false;
    return items.some((x) => PROCESSING_STATES.includes(String(x?.status)));
  }, [items, hasVideos, PROCESSING_STATES]);

  useEffect(() => {
    setPollMs(shouldPoll ? 10000 : 0);
  }, [shouldPoll]);

  const goToPageIndex = (pageIndex0) => {
    const nextPage = Number(pageIndex0) + 1;
    if (nextPage === currentPage) return;
    setCurrentPage(nextPage);
  };

  const pageMetaForComponent = useMemo(
    () => ({
      after: undefined,
      before: undefined,
      pageSize,
      hasNextPage: Boolean(meta?.hasNextPage),
      hasPrevPage: Boolean(meta?.hasPrevPage),
      totalItems,
    }),
    [meta?.hasNextPage, meta?.hasPrevPage, pageSize, totalItems]
  );

  const initialState = useMemo(
    () => ({ pageIndex: currentPage - 1 }),
    [currentPage]
  );

  const handleOpenPreview = (preview) => {
    setSelectedPreview(preview);
    onPreviewOpen();
  };

  useEffect(() => {
    setSelectedPreview(null);
  }, [currentPage, items.length]);

  return (
    <Box>
      {hasVideos && (
        <Flex align="center" justify="flex-end" mb={2} gap={4}>
          <Tooltip label="Refresh" fontSize="xs">
            <Flex _hover={{ color: 'gray.400' }} alignItems="center">
              <TbRefresh cursor="pointer" onClick={() => refetch()} size={20} />
            </Flex>
          </Tooltip>
          <Button size="sm" onClick={onOpen}>
            Upload to My Drive
          </Button>
        </Flex>
      )}

      <Flex>
        {isLoading ? (
          <Box w="full" minH="50vh" display="grid" placeItems="center">
            <Flex direction="column" gap={2} align="center">
              <Spinner />
              <Text>Loading…</Text>
            </Flex>
          </Box>
        ) : !hasVideos ? (
          <Box
            w="full"
            minH="40vh"
            display="grid"
            placeItems="center"
            px={4}
            textAlign="center"
          >
            <Box>
              <Text fontSize="lg" fontWeight="semibold" mb={1}>
                No generated videos yet
              </Text>
              <Text fontSize="sm" color="gray.500">
                Generate a video to see it here.
              </Text>
            </Box>
          </Box>
        ) : (
          <Box p={2} w="full">
            <SimpleGrid
              columns={{ base: 1, sm: 2, md: 3, xl: 5, '2xl': 6 }}
              spacing={4}
            >
              {items.map((preview, index) => {
                const id = String(preview?.media_job ?? preview?._id ?? index);
                return (
                  <RenderedVideoCard
                    key={id}
                    preview={preview}
                    ring={ring}
                    borderColor={borderColor}
                    onOpenPreview={handleOpenPreview}
                  />
                );
              })}
            </SimpleGrid>

            <Pagination
              PageMeta={pageMetaForComponent}
              initialState={initialState}
              isFetching={isFetching}
              onPageChange={({ currentPage: nextIndex0 }) => {
                const maxIndex0 = Math.max(0, Math.ceil(totalItems / pageSize) - 1);
                const clamped = Math.min(Math.max(nextIndex0, 0), maxIndex0);
                goToPageIndex(clamped);
              }}
            />
          </Box>
        )}
      </Flex>

      <UploadRenderVideosModal isOpen={isOpen} onClose={onClose} />

      <Modal isOpen={isPreviewOpen} onClose={onPreviewClose} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{selectedPreview?.title || 'Preview'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody display="flex" justifyContent="center" pb={2}>
            {selectedPreview && (
              <AspectRatio ratio={16 / 9} maxW="800px" w="100%" maxH="70vh">
                <>
                  <Skeleton isLoaded={loaded} borderRadius="8px" />
                  <Box
                    as="video"
                    src={selectedPreview?.videoUrl}
                    controls
                    autoPlay
                    preload="metadata"
                    onLoadedData={() => setLoaded(true)}
                    style={{
                      width: '100%',
                      height: '100%',
                      borderRadius: 8,
                      objectFit: 'contain',
                      display: 'block'
                    }}
                  />
                </>
              </AspectRatio>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default OutputGenerated;
